import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApprovalDrawer from './ApprovalDrawer';
import { useSession } from '../auth/SessionProvider';
import { bulkDecide, getQueue, getWorkflowSummary, listGates } from '../../services/workflowApi';
import './Approvals.css';

/*
 * The review queue.
 *
 * Four gates run in parallel, so the tabs are a filter rather than a sequence:
 * an approver works the one gate they hold the role for. "Assigned to me"
 * reflects the routing rules; it is a convenience, not a restriction, because
 * any role-holder may act on an unrouted gate.
 */

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { id: 'pending,in_review', label: 'Open' },
  { id: 'pending', label: 'Unclaimed' },
  { id: 'in_review', label: 'In review' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'approved', label: 'Approved' },
];

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const scopeLabel = (row) => {
  if (row.market_code) return row.market_code;
  if (row.locale_group && row.locale_group !== '*') return row.locale_group;
  return 'Global';
};

export default function ApprovalsQueue() {
  const { principal, signedIn } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const [gates, setGates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [openGate, setOpenGate] = useState(null);
  const [page, setPage] = useState(0);
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') || '');

  const gate = searchParams.get('gate') || '';
  const status = searchParams.get('status') || 'pending,in_review';
  const mine = searchParams.get('mine') === '1';
  const stale = searchParams.get('stale') === '1';
  const search = searchParams.get('search') || '';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '' || value === false) next.delete(key);
    else next.set(key, value === true ? '1' : value);
    setSearchParams(next, { replace: true });
    setPage(0);
    setSelected(new Set());
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, status };
      if (gate) params.gate = gate;
      if (mine) params.assignee = 'me';
      if (stale) params.stale = true;
      if (search) params.search = search;
      const [queue, sum] = await Promise.all([getQueue(params), getWorkflowSummary()]);
      setRows(queue.rows || []);
      setTotal(queue.total || 0);
      setSummary(sum);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the approval queue.');
    } finally {
      setLoading(false);
    }
  }, [gate, status, mine, stale, search, page]);

  useEffect(() => {
    listGates().then(setGates).catch(() => setGates([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const canAct = useMemo(() => {
    const held = new Set(principal?.roles || []);
    const admin = principal?.is_admin;
    return (row) => signedIn && (admin || !row.required_role || held.has(row.required_role));
  }, [principal, signedIn]);

  const actionable = rows.filter(canAct);
  const allSelected = actionable.length > 0 && actionable.every((r) => selected.has(r.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(actionable.map((r) => r.id)));

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const result = await bulkDecide(ids, action);
      const done = result.succeeded?.length || 0;
      const failed = result.failed?.length || 0;
      setToast(
        failed
          ? `${done} ${action}d, ${failed} could not be — check your roles for those gates.`
          : `${done} gate${done === 1 ? '' : 's'} ${action}d.`,
      );
      setSelected(new Set());
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || `Bulk ${action} failed.`);
    }
  };

  const pages = Math.ceil(total / PAGE_SIZE) || 1;

  return (
    <div className="apr-page">
      <header className="oc-header">
        <div className="oc-header-text">
          <h1>Approvals</h1>
          <p>
            Enrichment, imagery, pricing and spares are reviewed independently. A product is ready
            to publish once every gate that applies to it is approved.
          </p>
        </div>
      </header>

      {!signedIn && (
        <div className="apr-alert warn">
          You are browsing with the shared API key. Sign in to claim and approve — an approval has
          to be attributable to a person.
        </div>
      )}
      {error && <div className="apr-alert bad">{error}</div>}
      {toast && <div className="apr-alert ok">{toast}</div>}

      <div className="apr-tabs">
        <button
          type="button"
          className={`apr-tab${!gate ? ' on' : ''}`}
          onClick={() => setParam('gate', '')}
        >
          All gates
          {summary && <span className="apr-tab-count">{summary.open_total}</span>}
        </button>
        {gates.map((g) => (
          <button
            key={g.code}
            type="button"
            className={`apr-tab${gate === g.code ? ' on' : ''}`}
            onClick={() => setParam('gate', g.code)}
          >
            {g.label}
            {summary?.by_gate?.[g.code]?.open > 0 && (
              <span className="apr-tab-count">{summary.by_gate[g.code].open}</span>
            )}
          </button>
        ))}
      </div>

      <div className="apr-filters">
        <div className="oc-chips">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`oc-chip${status === f.id ? ' on' : ''}`}
              onClick={() => setParam('status', f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="oc-chips">
          <button
            type="button"
            className={`oc-chip${mine ? ' on' : ''}`}
            onClick={() => setParam('mine', !mine)}
            disabled={!signedIn}
            title={signedIn ? undefined : 'Sign in to see items routed to you'}
          >
            Assigned to me
            {summary?.assigned_to_me > 0 && <span className="apr-tab-count">{summary.assigned_to_me}</span>}
          </button>
          <button
            type="button"
            className={`oc-chip${stale ? ' on' : ''}`}
            onClick={() => setParam('stale', !stale)}
            title="Approved, then the values they cover changed"
          >
            Changed since approval
          </button>
        </div>
        <form
          className="apr-search"
          onSubmit={(e) => {
            e.preventDefault();
            setParam('search', searchDraft.trim());
          }}
        >
          <input
            className="oc-input"
            placeholder="SKU or name — wildcards * and ? (244*, 244?1)"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </form>
      </div>

      {selected.size > 0 && (
        <div className="apr-bulk">
          <span>{selected.size} selected</span>
          <button type="button" className="oc-btn" onClick={() => runBulk('reject')}>
            Reject
          </button>
          <button type="button" className="oc-btn oc-btn-primary" onClick={() => runBulk('approve')}>
            Approve
          </button>
          <button type="button" className="apr-linkbtn" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      <div className="oc-table-wrap">
        <table className="oc-table apr-table">
          <thead>
            <tr>
              <th className="apr-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={!actionable.length}
                  aria-label="Select all actionable rows"
                />
              </th>
              <th>SKU</th>
              <th>Product</th>
              <th>Gate</th>
              <th>Scope</th>
              <th>Status</th>
              <th>Missing</th>
              <th>Assignee</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowActionable = canAct(row);
              return (
                <tr
                  key={row.id}
                  className={row.is_stale ? 'apr-row stale' : 'apr-row'}
                  onClick={() => setOpenGate(row)}
                >
                  <td className="apr-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                      disabled={!rowActionable}
                      title={rowActionable ? undefined : `Requires the ${row.required_role} role`}
                      aria-label={`Select ${row.sku}`}
                    />
                  </td>
                  <td className="apr-sku">{row.sku}</td>
                  <td className="apr-name">{row.product_name || '—'}</td>
                  <td>{row.gate_label}</td>
                  <td>{scopeLabel(row)}</td>
                  <td>
                    <span className={`apr-chip ${row.status}`}>{row.status.replace('_', ' ')}</span>
                    {row.is_stale && <span className="apr-chip stale">changed</span>}
                  </td>
                  <td className="apr-missing">
                    {(row.missing_attributes || []).length
                      ? row.missing_attributes.join(', ')
                      : '—'}
                  </td>
                  <td>{row.assignee_name || row.assignee_email || <span className="apr-muted">unassigned</span>}</td>
                  <td className="apr-time">{fmtTime(row.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && rows.length === 0 && (
        <div className="oc-empty">
          <div className="oc-empty-title">Nothing to review</div>
          <div className="oc-empty-sub">
            No gates match these filters. Try widening the status or clearing “Assigned to me”.
          </div>
        </div>
      )}

      {loading && <p className="apr-muted apr-loading">Loading…</p>}

      {total > PAGE_SIZE && (
        <div className="oc-pager">
          <span className="oc-pager-note">
            Page {page + 1} of {pages} · {total} gates
          </span>
          <div className="oc-pager-btns">
            <button
              type="button"
              className="oc-btn"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="oc-btn"
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {openGate && (
        <ApprovalDrawer
          gate={openGate}
          onClose={() => setOpenGate(null)}
          onChanged={(message) => {
            setToast(message);
            load();
          }}
        />
      )}
    </div>
  );
}
